/**
 * InvoiceImportSection — bulk-import accountant fee invoices (#17, Inc 5e-4).
 *
 * The "Import invoices" tab of /admin/fees (Admin-only). Upload the accountant's
 * end-of-month Excel + the invoice PDFs, Preview (no writes) the per-row match +
 * ±EUR1 cross-check, then Import (commit) — creates Issued invoices for every
 * importable row. Stateless: the chosen files stay in the browser and are re-sent
 * on commit (the backend import endpoint is stateless). Mismatched amounts are
 * imported + flagged (not blocked — MIS posture, C-227).
 */
import { useState } from 'react';
import {
  Alert, Badge, Button, Checkbox, Group, Modal, Stack, Table, Text, ThemeIcon,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { notifications } from '@mantine/notifications';
import {
  IconAlertTriangle, IconCheck, IconFileTypePdf, IconUpload, IconX,
} from '@tabler/icons-react';

import { XlsxDropzone } from './XlsxDropzone';
import { feeInvoicesApi, type InvoiceImportReport } from '../api/feeInvoices';
import { fmtEurFromCents } from '../api/feeSchedules';

// Friendly match-method labels + colours (§3j).
const METHOD_LABEL: Record<string, string> = {
  indirect_fund: 'Fund #',
  direct_mnemonic: 'Bank mnemonic',
  single_account: 'Sole account',
  value_inferred: 'By amount',
  no_account: 'Assign manually',
  no_agreement: 'No agreement',
};
const METHOD_COLOR: Record<string, string> = {
  indirect_fund: 'teal',
  direct_mnemonic: 'teal',
  single_account: 'blue',
  value_inferred: 'yellow',
  no_account: 'orange',
  no_agreement: 'red',
};

const fmt = (c: number | null) => (c == null ? '—' : fmtEurFromCents(c));
const fmtMonth = (iso: string) => {
  const [y, m] = iso.split('-');
  return new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });
};

export function InvoiceImportSection() {
  const [excel, setExcel] = useState<File | null>(null);
  const [pdfs, setPdfs] = useState<File[]>([]);
  const [report, setReport] = useState<InvoiceImportReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [overwrite, setOverwrite] = useState(false);

  const committed = report?.mode === 'commit';
  const actionable = report ? report.importable + report.overwritable : 0;
  // Matched rows already carrying an invoice — the dead-end that turning Overwrite on resolves (§3j).
  const existingCount = report ? report.rows.filter((r) => r.existing).length : 0;

  const run = async (mode: 'preview' | 'commit', ov: boolean = overwrite) => {
    if (!excel) return;
    setLoading(true);
    try {
      const rep = await feeInvoicesApi.importInvoices(excel, pdfs, mode, ov);
      setReport(rep);
      if (mode === 'commit') {
        const parts = [`Imported ${rep.created} invoice${rep.created === 1 ? '' : 's'}`];
        if (rep.overwritten) parts.push(`${rep.overwritten} overwritten`);
        if (rep.skipped_existing) parts.push(`${rep.skipped_existing} already existed`);
        notifications.show({ color: 'green', message: parts.join(' · ') });
      }
    } catch (e) {
      notifications.show({
        color: 'red',
        title: 'Import failed',
        message: e instanceof Error ? e.message : 'Unexpected error.',
      });
    } finally {
      setLoading(false);
      setConfirmOpen(false);
    }
  };

  const resetReport = () => setReport(null);

  return (
    <Stack gap="lg">
      <Text size="sm" c="dimmed">
        Upload the accountant's end-of-month Excel and the matching invoice PDFs. <b>Preview</b> shows
        how each invoice matches an account and cross-checks the amounts against the computed fee;
        nothing is saved until you press <b>Import</b>.
      </Text>

      <Group grow align="flex-start">
        <Stack gap="xs">
          <Text fw={600} size="sm">1 · Accountant Excel</Text>
          <XlsxDropzone
            hint="One Excel file: the accountant's end-of-month invoice list."
            onSelect={(f) => { setExcel(f); resetReport(); }}
            loading={loading}
            onReject={() => notifications.show({ color: 'red', message: 'Please choose a single .xlsx file.' })}
          />
          {excel && <Text size="sm">Selected: <b>{excel.name}</b></Text>}
        </Stack>

        <Stack gap="xs">
          <Text fw={600} size="sm">2 · Invoice PDFs</Text>
          <Dropzone
            onDrop={(fs) => { setPdfs(fs); resetReport(); }}
            accept={{ 'application/pdf': ['.pdf'] }}
            multiple
            loading={loading}
          >
            <Group justify="center" gap="xl" mih={140} style={{ pointerEvents: 'none' }}>
              <Dropzone.Accept><IconUpload size={42} stroke={1.5} /></Dropzone.Accept>
              <Dropzone.Reject><IconX size={42} stroke={1.5} /></Dropzone.Reject>
              <Dropzone.Idle><IconFileTypePdf size={42} stroke={1.5} /></Dropzone.Idle>
              <div>
                <Text size="lg" inline>Drag the invoice PDFs here, or click to choose</Text>
                <Text size="sm" c="dimmed" inline mt={7}>
                  Many PDFs — named <code>FT_FA_YYYY_###_…</code> (matched to each invoice row).
                </Text>
              </div>
            </Group>
          </Dropzone>
          {pdfs.length > 0 && <Text size="sm">{pdfs.length} PDF{pdfs.length === 1 ? '' : 's'} selected</Text>}
        </Stack>
      </Group>

      <Group>
        <Button onClick={() => run('preview')} disabled={!excel || loading} loading={loading && !confirmOpen}>
          Preview
        </Button>
        <Checkbox
          label="Overwrite existing invoices"
          checked={overwrite}
          disabled={loading}
          onChange={(e) => {
            const ov = e.currentTarget.checked;
            setOverwrite(ov);
            // Re-preview with the new mode so the create-vs-overwrite counts update (§3j).
            if (excel && report && !committed) run('preview', ov);
          }}
        />
        {report && !committed && actionable > 0 && (
          <Button color="green" onClick={() => setConfirmOpen(true)} disabled={loading}>
            {report.overwritable > 0
              ? `Import ${report.importable} · overwrite ${report.overwritable}`
              : `Import ${report.importable} invoice${report.importable === 1 ? '' : 's'}`}
          </Button>
        )}
      </Group>

      {report && !committed && actionable === 0 && !overwrite && existingCount > 0 && (
        <Alert color="yellow" variant="light" title="These invoices already exist">
          <Text size="sm">
            {existingCount} matched row{existingCount === 1 ? '' : 's'} already{' '}
            {existingCount === 1 ? 'has' : 'have'} an invoice for that account-month, so there is nothing new to create.
            Turn on <b>Overwrite existing invoices</b> above to amend the issued ones (amounts + PDF replaced);
            settled invoices are never overwritten.
          </Text>
        </Alert>
      )}

      {report && <ReportView report={report} committed={committed} />}

      <Modal opened={confirmOpen} onClose={() => setConfirmOpen(false)} title="Import invoices?" centered>
        <Stack>
          <Text size="sm">
            This will upload <b>{report?.importable}</b> issued invoice
            {report?.importable === 1 ? '' : 's'}
            {report && report.overwritable > 0
              ? <> and <b>overwrite {report.overwritable}</b> existing issued invoice{report.overwritable === 1 ? '' : 's'} (amounts + PDF replaced)</>
              : null}
            {' '}— the accountant's invoices (their PDFs + amounts); it does not originate new invoices.
            Amount mismatches are uploaded and flagged (not blocked); settled invoices are never overwritten.
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="green" loading={loading} onClick={() => run('commit')}>Import</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function ReportView({ report, committed }: { report: InvoiceImportReport; committed: boolean }) {
  const warn = report.unmatched > 0 || report.mismatches > 0 || report.pdf_missing > 0;
  // Exceptions summary (§3j) — surface the rows needing attention IN THE BANNER so the operator
  // never has to scan the full table to find a cross-check Diff / missing PDF / unmatched row.
  const rowLabel = (r: InvoiceImportReport['rows'][number]) =>
    [r.invoice_number, r.agreement_code, r.account_label].filter(Boolean).join(' · ')
    || r.reference || `row ${r.row_index}`;
  const eur = (c: number | null) => (c == null ? '—' : `€${fmtEurFromCents(c)}`);
  const mismatchRows = report.rows.filter((r) => r.net_mismatch || r.total_mismatch);
  const missingPdfRows = report.rows.filter((r) => r.matched && !r.pdf_found);
  const unmatchedRows = report.rows.filter((r) => !r.matched);
  const pdfMismatchRows = report.rows.filter((r) => r.pdf_amount_mismatch);
  const clean = mismatchRows.length === 0 && missingPdfRows.length === 0
    && unmatchedRows.length === 0 && pdfMismatchRows.length === 0;
  return (
    <Stack gap="sm">
      <Alert color={warn ? 'yellow' : 'teal'} title={committed ? 'Import complete' : 'Preview'}>
        <Stack gap="xs">
          <Group gap="lg">
            <Text size="sm">{report.fa_rows} invoice row{report.fa_rows === 1 ? '' : 's'}</Text>
            <Text size="sm">{report.matched} matched · {report.unmatched} unmatched</Text>
            {report.pdf_missing > 0 && <Text size="sm" c="orange">{report.pdf_missing} missing PDF</Text>}
            {report.mismatches > 0 && <Text size="sm" c="red">{report.mismatches} amount mismatch</Text>}
            {report.pdf_mismatches > 0 && <Text size="sm" c="red">{report.pdf_mismatches} PDF≠Excel</Text>}
            {report.nc_skipped > 0 && <Text size="sm" c="dimmed">{report.nc_skipped} credit note{report.nc_skipped === 1 ? '' : 's'} skipped</Text>}
            {committed
              ? <Text size="sm" fw={600}>{report.created} uploaded{report.overwritten ? ` · ${report.overwritten} overwritten` : ''} · {report.skipped_existing} skipped</Text>
              : <Text size="sm" fw={600}>{report.importable} ready to import{report.overwritable ? ` · ${report.overwritable} to overwrite` : ''}</Text>}
          </Group>

          {report.coverage.length > 0 && (
            <Group gap="md">
              <Text size="sm" fw={700}>Coverage:</Text>
              {report.coverage.map((c) => (
                <Text key={c.period_month} size="sm" c={c.not_invoiced > 0 ? 'orange.8' : 'teal.8'}>
                  {fmtMonth(c.period_month)} — {c.invoiced}/{c.billed} billed account-months invoiced
                  {c.not_invoiced > 0 ? ` · ${c.not_invoiced} not yet invoiced` : ' · complete'}
                </Text>
              ))}
            </Group>
          )}

          {committed && report.coverage.length > 0 && (
            <Group gap="xs">
              <Text size="sm" fw={700}>Received Excel retained:</Text>
              {report.coverage.map((c) => (
                <Button
                  key={c.period_month}
                  variant="light" size="compact-xs"
                  onClick={() => feeInvoicesApi.downloadReceivedFile(c.period_month.slice(0, 7))
                    .catch((e) => notifications.show({ color: 'red', message: e instanceof Error ? e.message : 'Download failed.' }))}
                >
                  {fmtMonth(c.period_month)} ↓
                </Button>
              ))}
            </Group>
          )}

          {clean ? (
            <Group gap={6}>
              <IconCheck size={15} color="var(--mantine-color-teal-7)" />
              <Text size="sm" c="teal.8">No deviations — every row matched an account, cross-checked within €1, and has its PDF.</Text>
            </Group>
          ) : (
            <Stack gap={8}>
              <Text size="sm" fw={700}>Needs attention:</Text>
              {mismatchRows.length > 0 && (
                <div>
                  <Text size="sm" fw={600} c="red.8">
                    {mismatchRows.length} amount mismatch{mismatchRows.length === 1 ? '' : 'es'} (invoiced vs computed, over €1):
                  </Text>
                  <Stack gap={0} pl="sm">
                    {mismatchRows.map((r, i) => (
                      <Text key={i} size="xs">
                        {rowLabel(r)} — invoiced {eur(r.invoiced_total_cents)} vs computed {eur(r.computed_total_cents)}
                      </Text>
                    ))}
                  </Stack>
                </div>
              )}
              {missingPdfRows.length > 0 && (
                <div>
                  <Text size="sm" fw={600} c="orange.8">
                    {missingPdfRows.length} matched invoice{missingPdfRows.length === 1 ? '' : 's'} with no PDF{committed ? ' (not uploaded)' : ' (won’t import)'}:
                  </Text>
                  <Stack gap={0} pl="sm">
                    {missingPdfRows.map((r, i) => <Text key={i} size="xs">{rowLabel(r)}</Text>)}
                  </Stack>
                </div>
              )}
              {unmatchedRows.length > 0 && (
                <div>
                  <Text size="sm" fw={600} c="red.8">
                    {unmatchedRows.length} unmatched row{unmatchedRows.length === 1 ? '' : 's'} (won’t import):
                  </Text>
                  <Stack gap={0} pl="sm">
                    {unmatchedRows.map((r, i) => (
                      <Text key={i} size="xs">{rowLabel(r)}{r.note ? ` — ${r.note}` : ''}</Text>
                    ))}
                  </Stack>
                </div>
              )}
              {pdfMismatchRows.length > 0 && (
                <div>
                  <Text size="sm" fw={600} c="red.8">
                    {pdfMismatchRows.length} PDF amount{pdfMismatchRows.length === 1 ? '' : 's'} ≠ the Excel total (imported + flagged, not blocked):
                  </Text>
                  <Stack gap={0} pl="sm">
                    {pdfMismatchRows.map((r, i) => (
                      <Text key={i} size="xs">
                        {rowLabel(r)} — PDF {eur(r.pdf_amount_cents)} vs Excel {eur(r.invoiced_total_cents)}
                      </Text>
                    ))}
                  </Stack>
                </div>
              )}
            </Stack>
          )}
        </Stack>
      </Alert>

      {report.errors.length > 0 && (
        <Alert color="red" title="Parse warnings">
          <Stack gap={2}>{report.errors.map((e, i) => <Text key={i} size="xs">{e}</Text>)}</Stack>
        </Alert>
      )}

      <Table striped withTableBorder horizontalSpacing="sm" verticalSpacing={6}>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Invoice #</Table.Th>
            <Table.Th>Agreement</Table.Th>
            <Table.Th>Account</Table.Th>
            <Table.Th>Match</Table.Th>
            <Table.Th ta="right">Computed</Table.Th>
            <Table.Th ta="right">Invoiced</Table.Th>
            <Table.Th>Cross-check</Table.Th>
            <Table.Th ta="center">PDF</Table.Th>
            <Table.Th>{committed ? 'Result' : 'Status'}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {report.rows.map((r, i) => (
            <Table.Tr key={i}>
              <Table.Td>{r.invoice_number ?? '—'}</Table.Td>
              <Table.Td>{r.agreement_code ?? '—'}</Table.Td>
              <Table.Td>{r.account_label ?? <Text c="dimmed" size="sm">—</Text>}</Table.Td>
              <Table.Td>
                <Badge color={METHOD_COLOR[r.method] ?? 'gray'} variant="light" size="sm">
                  {METHOD_LABEL[r.method] ?? r.method}
                </Badge>
              </Table.Td>
              <Table.Td ta="right">{fmt(r.computed_total_cents)}</Table.Td>
              <Table.Td ta="right">{fmt(r.invoiced_total_cents)}</Table.Td>
              <Table.Td>
                {r.net_mismatch || r.total_mismatch
                  ? <Badge color="red" variant="light" size="sm" leftSection={<IconAlertTriangle size={12} />}>Diff</Badge>
                  : r.computed_total_cents != null
                    ? <Badge color="teal" variant="light" size="sm">OK</Badge>
                    : <Text c="dimmed" size="xs">no run</Text>}
              </Table.Td>
              <Table.Td ta="center">
                {r.pdf_found
                  ? <ThemeIcon color="teal" variant="light" size="sm"><IconCheck size={14} /></ThemeIcon>
                  : <ThemeIcon color="orange" variant="light" size="sm"><IconX size={14} /></ThemeIcon>}
              </Table.Td>
              <Table.Td>
                {committed
                  ? (r.importable
                      ? <Badge color="green" size="sm">Uploaded</Badge>
                      : r.will_overwrite
                        ? <Badge color="blue" size="sm">Overwritten</Badge>
                        : <Text size="xs" c="dimmed">{r.note ?? 'Skipped'}</Text>)
                  : (r.importable
                      ? <Badge color="green" variant="light" size="sm">Ready</Badge>
                      : r.will_overwrite
                        ? <Badge color="blue" variant="light" size="sm">Overwrite</Badge>
                        : <Text size="xs" c="dimmed">{r.note ?? '—'}</Text>)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    </Stack>
  );
}
